 <!-- SOURCE: https://medium.com/tyrone-tudehope/composing-d3-visualizations-with-vue-js-c65084ccb686 -->
<template>
  <div
    :style="{height: height + 'px', width: width + 'px'}"
    class="funnel-chart mt-3"
    :class="{disabled: disabled}"
  >
    <h2 class="text-black mb-3">
      Readership Funnel
    </h2>

    <div class="funnel-chart__wrapper">
      <svg
        v-if="isReady"
        :width="svgWidth"
        :height="svgHeight"
      >
        <g :transform="`translate(${margin.left},${margin.top})`">
          <g
            v-if="scaleAxisX && items"
            :transform="`translate(0,${paddedHeight})`"
          >
            <axis-x
              :scale-x="scaleAxisXLabel"
              :showTickText="true"
              :width="paddedWidth"
              :type="'funnelLabel'"
              :disabled="disabled"
            />

            <axis-x
              :v-if="!disabled"
              :scale-x="scaleAxisXPerc"
              :showTickText="true"
              :width="paddedWidth"
              :type="'funnelPerc'"
              :disabled="disabled"
            />

            <axis-x
              :v-if="!disabled"
              :scale-x="scaleAxisXNum"
              :showTickText="true"
              :width="paddedWidth"
              :type="'funnelNum'"
              :disabled="disabled"
            />

            <axis-x
              :scale-x="scaleAxisX"
              :showTickText="false"
              :width="paddedWidth"
              :height="paddedHeight"
              :type="'vertGrid'"
              :disabled="disabled"
            />
          </g>

          <g v-if="scaleY && items">
            <axis-y
              :scale-y="scaleY"
              :ceil="100"
              :width="paddedWidth"
              :height="paddedHeight"
              :type="'percentage'"
              :disabled="disabled"
            />
          </g>

          <g
            v-if="paths && !disabled"
            :style="{height: paddedHeight}"
          >
            <path
              v-for="(path, key) in paths"
              :id="key"
              :key="key"
              :index="key"
              :d="path['data']"
              :class="path['type']"
              :style="path['style']"
            />
          </g>
        </g>
      </svg>

      <div
        v-else
        class="loading-wrapper"
        :style="{height: svgHeight + 'px', width: svgWidth + 'px'}"
      >
        <loading-icon
          :loading="!isReady"
          loading-class="text-center vertical-middle"
        />
      </div>
    </div>

    <div
      v-if="legendItems && items && !disabled"
      class="legend-wrapper"
    >
      <category-legend
        :items="legendItems"
        :items-active="items"
        :client-width="clientWidth"
      />
    </div>
  </div>
</template>

<script>
import d3 from '@/analytics/importers/d3-importer'
import ChartMixin from '@/analytics/mixins/ChartMixin'
import {capitalise} from '@/analytics/utils/data-analyser-utils'

import AxisX from './AxisX'
import AxisY from './AxisY'
import CategoryLegend from './CategoryLegend'

const props = {
  margin: {
    type: Object,
    default: () => ({
      left: 60,
      right: 40,
      top: 10,
      bottom: 75,
    }),
  },
  processed: {
    type: Object,
    default: () => ({
      primary: 7050,
      channel: 8000,
    }),
  },
  type: {
    type: String,
  },
  labelsLong: {
    type: Array,
    required: true,
  },
  labelsShort: {
    type: Array,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  clientWidth: {
    type: Number,
    default: null,
  },
  minWidth: {
    type: Number,
    default: null,
  },
}

export default {
  name: 'FunnelChart',
  components: {
    AxisX,
    AxisY,
    CategoryLegend,
  },
  mixins: [ChartMixin],
  props,
  data () {
    return {
      paths: {},
      points: {},
      legendItems: {
        [this.type]: {name: 'This ' + capitalise(this.type), color: '#62ddc7'},
        channel: {name: 'Channel Average', color: '#0364f6'},
        network: {name: 'Network Average', color: '#191364'},
      },
    }
  },
  computed: {
    svgWidth () {
      return Math.max(this.minWidth, this.width - 26)
    },
    svgHeight () {
      return this.disabled
        ? this.height - 24 * 2
        : this.height - 24 * 2 - 100
    },
    paddedWidth () {
      return this.svgWidth - this.margin.left - this.margin.right
    },
    paddedHeight () {
      return this.svgHeight - this.margin.top - this.margin.bottom
    },
    scaleX () {
      return d3.scalePoint()
          .range([0, this.paddedWidth])
          .domain(this.intervalsX())
    },
    scaleY () {
      return d3.scaleLinear()
          .range([this.paddedHeight, 0])
          .domain(this.intervalsY())
    },
    scaleAxisXLabel () {
      return d3.scaleBand()
          .range([0, this.paddedWidth])
          .domain(this.intervalsX('funnelLabel'))
    },
    scaleAxisXPerc () {
      return d3.scaleBand()
          .range([0, this.paddedWidth])
          .domain(this.intervalsX('funnelPerc'))
    },
    scaleAxisXNum () {
      return d3.scaleBand()
          .range([0, this.paddedWidth])
          .domain(this.intervalsX('funnelNum'))
    },
    scaleAxisX () {
      return d3.scalePoint()
          .range([0, this.paddedWidth])
          .domain(this.intervalsX())
    },
    firstItems () {
      return this.items[this.type]
    },
    isReady () {
      return (this.items) != null
    },
    firstItemsCeil () {
      return Math.max(...this.firstItems.map((x, i) => x), 0)
    },
    ceil () {
      return Math.max(this.firstItemsCeil, this.processed['primary'], 0)
    },
  },
  methods: {
    intervalsX (type = 'points') {
      if (type === 'funnelLabel') {
        return this.labelsLong
      } else if (type === 'funnelPerc') {
        console.log(this.firstItems)
        const label = this.firstItemsCeil === this.ceil && this.firstItemsCeil > this.processed['primary'] ? this.labelsShort[Math.max(this.firstItems.indexOf(this.firstItemsCeil), 0)] : 'SENT'

        return this.firstItems.slice(0, -1).map((x, i) => {
          return this.ceil > 0
            ? `${this.toPerc(x)}% OF ${label}_${i}`
            : `NO DATA_${i}`
        })
      } else if (type === 'funnelNum') {
        return this.firstItems.slice(0, -1).map((x, i) => x + '_' + i)
      } else {
        return this.firstItems.map((x, i) => i)
      }
    },
    intervalsY (type = 'percentages') {
      return [0, 100]
    },
    createArea: d3.area().x((d) => d.x).y0((d) => d.max).y1((d) => d.y),
    createLine: d3.line().x((d) => d.x).y((d) => d.y),
    toPerc (num, type = 'article') {
      const perc = this.ceil > 0 ? Math.round(num / this.ceil * 100) : 0
      return num > 0 ? Math.max(1, perc) : perc
    },
    drawChart () {
      if (!this.items) {
        return
      }

      for (const [prop, its] of Object.entries(this.items)) {
        const locPoints = []
        for (const [i, d] of its.entries()) {
          locPoints.push({
            x: this.scaleX(i),
            y: this.scaleY(this.toPerc(d, prop)),
            max: this.paddedHeight,
          })
        }

        this.$set(this.points, prop + '-line', locPoints)
        this.$set(this.paths, prop + '-line', {type: 'line', prop: prop, data: this.createLine(locPoints), style: {stroke: this.getColor(prop), strokeWidth: 2, fill: 'none'}})

        this.$set(this.paths, prop + '-area', {type: 'area', prop: 'area', data: this.createArea(locPoints), style: {fill: this.getColor(prop), fillOpacity: 0.15}})
      }
    },
  },
}
</script>

<style lang="scss" scoped>
  .funnel-chart {
    padding: 24px 0 24px 24px;
    border: 1px solid #dfe2ee;
    border-radius: 2px;
    display: inline-block;
    overflow: hidden;
    &__wrapper {
      overflow: hidden;
      overflow-x: auto;
      svg {
        margin-bottom: 4px;
      }
    }

    &.disabled {
      color: #98a0bc !important;
      svg {
        cursor: no-drop;
      }
      div, span, h2, text {
        color: #98a0bc !important;
      }
      .axis-wrapper {
        color: #98a0bc !important;
        &.funnelPerc, &.funnelNum {
          display : none;
        }
      }
    }

    .legend-wrapper {
      margin: 8px 0 0 24px;
    }
  }

</style>
